<template>
    <div class="w-full h-full">
        <div class="manual-steps w-full h-full">
            <!-- Step -1: Missing card request success -->
            <ManualCardSuccess
                v-show="manualStep === -1"
                @close="$emit('close')"
            />

            <!-- Steps 0 & 1: Card search / missing card form -->
            <ManualCardSearch
                v-show="manualStep === 0 || manualStep === 1"
                :manual-card="manualCard"
                :manual-chosen-card="manualChosenCard"
                :field-errors="fieldErrors"
                :errors="errors"
                :add-card-btn-action="addCardBtnAction"
                :current-step="manualStep"
                :user="user"
                :has-multiple-users="hasMultipleUsers"
                @close="$emit('close')"
                @close-missing-card="closeMissingCardModal"
                @goto-step="gotoStep"
                @goto-manual-step="gotoManualStep"
                @add-card="handleAddCard"
                @card-selected="cardSelected"
                @clear-selected-card="clearSelectedCard"
                @open-missing-card="openMissingCardModal"
                @submit-missing-card="handleSubmitMissingCard"
                @clear-field-error="clearFieldError"
                @update:current-step="manualStep = $event"
            />

            <!-- Steps 2 & 3: Date opened / card details -->
            <ManualCardDetails
                v-show="manualStep === 2 || manualStep === 3"
                :manual-card="manualCard"
                :manual-chosen-card="manualChosenCard"
                :field-errors="fieldErrors"
                :errors="errors"
                :current-step="manualStep"
                :selected-card-and-user-header="selectedCardAndUserHeader"
                :businesses="businesses"
                :account-user-for-card="accountUserForCard"
                @close="$emit('close')"
                @goto-manual-step="gotoManualStep"
                @add-card="handleAddCard"
                @clear-field-error="clearFieldError"
                @open-add-entity-modal="openAddEntityModal"
                @update:credit-limit="manualCard.creditLimit = $event"
                @update:business_id="manualCard.business_id = $event"
            />

            <!-- Step 4: Welcome bonus -->
            <ManualCardBonus
                v-show="manualStep === 4"
                :manual-chosen-card="manualChosenCard"
                v-model:welcome-bonus="welcomeBonus"
                v-model:tiered-bonuses="tieredBonuses"
                :errors="errors"
                :field-errors="fieldErrors"
                :selected-card-and-user-header="selectedCardAndUserHeader"
                @close="$emit('close')"
                @goto-manual-step="gotoManualStep"
                @add-card="handleAddCard"
                @reset-bonuses="handleResetBonuses"
            />
        </div>
    </div>

    <!-- Business Entity Modal -->
    <BusinessEntitiesAddEditModal
        :open="showAddEntityModal"
        :users="[user]"
        v-model:business-model="newEntityModel"
        @close="closeAddEntityModal"
        @saved="handleEntitySaved"
    />
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useWalletStore } from '@/store/wallet.js';
import { useManualCardWizard } from '@/composables/useManualCardWizard.js';
import { useCardSubmission } from '@/composables/useCardSubmission.js';
import BusinessEntitiesAddEditModal from '@/components/business-entities/business-entities-add-edit-modal.vue';
import ManualCardSuccess from './manual-card-entry/ManualCardSuccess.vue';
import ManualCardSearch from './manual-card-entry/ManualCardSearch.vue';
import ManualCardDetails from './manual-card-entry/ManualCardDetails.vue';
import ManualCardBonus from './manual-card-entry/ManualCardBonus.vue';

const props = defineProps({
    accountUserForCard: Number,
    wallet: Object,
    cards: Array,
    banks: Array,
});

const emit = defineEmits(['close', 'finished', 'gotoStep', 'setProgress', 'showAddBusinessModal']);

const walletStore = useWalletStore();
const { users } = storeToRefs(walletStore);

const {
    manualStep,
    errors,
    fieldErrors,
    addCardBtnAction,
    manualCard,
    manualChosenCard,
    gotoManualStep,
    validateStep3,
    clearFieldError,
    cardSelected,
    clearSelectedCard,
    resetManualCard,
} = useManualCardWizard(emit);

const { addCard, submitMissingCardRequest } = useCardSubmission();

// Welcome bonus state
const welcomeBonus = ref({
    type: null,
    minimum_spend_deadline: null,
    minimum_spend_amount: null,
    bonus_amount: null,
    bonus_units: '',
    date_received: null,
    received: false,
});

const tieredBonuses = ref([
    {
        type: null,
        tier_number: 1,
        bonus_amount: null,
        bonus_units: '',
        minimum_spend_amount: null,
        minimum_spend_deadline: null,
        date_received: null,
        received: false,
    },
]);

// Business entity modal state
const showAddEntityModal = ref(false);
const newEntityModel = ref({
    user_id: null,
    name: null,
    type: null,
    tax_id_type: null,
    annual_business_spend: null,
});

// Computed
const user = computed(() => {
    return (
        props.wallet?.users?.find((el) => el.id === props.accountUserForCard) ||
        { name: 'User', businesses: [] }
    );
});

const hasMultipleUsers = computed(() => {
    const walletUsers =
        props.wallet && Array.isArray(props.wallet.users)
            ? props.wallet.users
            : [];
    const storeUsers =
        users.value && Array.isArray(users.value) ? users.value : [];
    const count = walletUsers.length || storeUsers.length;
    return count > 1;
});

const businesses = computed(() => {
    if (!user.value || !user.value.businesses) {
        return [];
    }
    return user.value.businesses.map((el) => ({
        id: el.id,
        name: el.name,
    }));
});

const selectedCardAndUserHeader = computed(() => {
    if (!manualChosenCard.value) return '';
    const cardName =
        manualChosenCard.value.title || manualChosenCard.value.name || '';
    const last4 =
        manualCard.last_four
            ? String(manualCard.last_four).trim()
            : '';
    const suffix = last4 ? ` (${last4})` : '';
    const usersCount =
        props.wallet && props.wallet.users
            ? props.wallet.users.length
            : users.value && users.value.length
              ? users.value.length
              : 0;
    const userName = user.value && user.value.name ? user.value.name : '';
    const cardPart = `${cardName}${suffix}`;
    return usersCount > 1 && userName
        ? `${userName} • ${cardPart}`
        : cardPart;
});

// Watchers
watch(
    () => walletStore.selectedCard,
    (newCard) => {
        if (newCard && newCard.card_match_request) {
            const unresolvedCard =
                walletStore.wallet.unresolved_cards.find(
                    (card) => card.connected_account_id === newCard.id
                );
            if (unresolvedCard) {
                manualCard.card_not_found = true;
                manualCard.card_not_found_name =
                    unresolvedCard.card_not_found_name;
                manualCard.card_not_found_bank =
                    unresolvedCard.issuing_bank;
            }
        }
    },
    { immediate: true }
);

watch(
    () => manualCard.card_not_found,
    (newVal) => {
        if (newVal) {
            const existingBank = manualCard.card_not_found_bank;
            const existingName = manualCard.card_not_found_name;
            const existingLastFour = manualCard.last_four;

            Object.assign(manualCard, {
                chosenCard: null,
                last_four: existingLastFour,
                dateOpened: '',
                accountType: '',
                bonusAmount: '',
                minSpendDate: '',
                bonusReceived: '',
                dateReceived: '',
                cardStatus: '',
                dateClosed: '',
                creditLimit: 0,
                dateBonusEarned: '',
                currentRewardsBalance: '',
                institution_code: '',
                card_not_found: true,
                opened_long_ago: false,
                closed_long_ago: false,
                card_not_found_name: existingName,
                card_not_found_bank: existingBank,
                business_id: null,
            });
        }
    }
);

watch(
    () => manualCard.cardStatus,
    (newVal, oldVal) => {
        if (oldVal === 'closed' && newVal === 'open') {
            manualCard.dateClosed = '';
            manualCard.closed_long_ago = false;
            if (fieldErrors.date_closed) {
                delete fieldErrors.date_closed;
            }
        }
        if (newVal === 'closed') {
            if (fieldErrors.date_opened) {
                delete fieldErrors.date_opened;
            }
        }
    }
);

watch(
    () => manualCard.opened_long_ago,
    (newVal) => {
        if (newVal && fieldErrors.date_opened) {
            delete fieldErrors.date_opened;
        }
    }
);

// Methods
function gotoStep(step) {
    errors.value = [];
    emit('gotoStep', parseInt(step));
}

function handleAddCard() {
    // For authorized users finishing on step 3, run the same step 3 → 4
    // validation that gotoManualStep enforces before submitting.
    if (manualStep.value === 3 && manualCard.accountType === 'authorized_user') {
        if (!validateStep3()) return;
    }
    addCard({
        manualCard,
        manualChosenCard,
        welcomeBonus,
        tieredBonuses,
        accountUserForCard: props.accountUserForCard,
        walletId: props.wallet.id,
        addCardBtnAction,
        errors,
        onSuccess: () => {
            manualStep.value = 1;
            resetManualCard();
            emit('finished');
        },
        onResetBonuses: handleResetBonuses,
    });
}

function handleSubmitMissingCard() {
    submitMissingCardRequest({
        manualCard,
        fieldErrors,
        accountUserForCard: props.accountUserForCard,
        walletId: props.wallet.id,
        addCardBtnAction,
        errors,
        manualStep,
    });
}

function openMissingCardModal() {
    manualStep.value = 0;
}

function closeMissingCardModal() {
    manualStep.value = 1;
    resetManualCard();
    emit('finished');
}

function handleResetBonuses() {
    welcomeBonus.value = {
        type: null,
        minimum_spend_deadline: null,
        minimum_spend_amount: null,
        bonus_amount: null,
        bonus_units: '',
        date_received: null,
        received: false,
    };
    tieredBonuses.value = [
        {
            type: null,
            tier_number: 1,
            bonus_amount: null,
            bonus_units: '',
            minimum_spend_amount: null,
            minimum_spend_deadline: null,
            date_received: null,
            received: false,
        },
    ];
}

function openAddEntityModal() {
    newEntityModel.value.user_id = props.accountUserForCard;
    showAddEntityModal.value = true;
}

function closeAddEntityModal() {
    showAddEntityModal.value = false;
    newEntityModel.value = {
        user_id: null,
        name: null,
        type: null,
        tax_id_type: null,
        annual_business_spend: null,
    };
}

function handleEntitySaved(savedEntity) {
    closeAddEntityModal();
    if (savedEntity && savedEntity.id) {
        manualCard.business_id = savedEntity.id;
    }
}
</script>

<style lang="scss" scoped>
#manual-card-section {
    width: 100%;
}
.manual-steps {
    display: flex;
    flex-direction: column;
}
.manual-step {
    width: 100%;
}
.fadeInOut-enter-active,
.fadeInOut-leave-active {
    transition: opacity 0.8s;
    opacity: 1;
}
.fadeInOut-enter,
.fadeInOut-leave-to {
    opacity: 0;
}
</style>
